/**
 * Resolves a Pokémon sprite URL from the species NAME (as the user pleaded).
 *
 * Priority chain:
 *   1. Custom GIF uploaded by the teacher (matches by name in gif_configs)
 *   2. Official animated GIF from PokeAPI on GitHub (by Pokédex ID)
 *   3. Official static sprite from PokeAPI (by Pokédex ID) — for newer gens
 *      that lack animated GIFs.
 *   4. null — caller should render a safe placeholder (Pokeball spinner),
 *      never a random/unrelated Pokémon sprite.
 *
 * The lookup is case-insensitive and tolerates accents, dots, spaces,
 * and a handful of common typos found in the existing gif_configs.
 */

import pokemonIdMap from '../config/pokemonIdMap.json';

// ============ IMAGEKIT CDN (fonte primária das imagens dos alunos) ============
// Arquivos animados hospedados na raiz do ImageKit, nomeados pelo nome do
// Pokémon em minúsculas (ex: https://ik.imagekit.io/pokeclass/primarina.gif)
export const IMAGEKIT_URL = 'https://ik.imagekit.io/pokeclass/';

const POKEAPI_ANIMATED = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/versions/generation-v/black-white/animated';
const POKEAPI_STATIC = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon';

// Common typos / pt-br variants observed in the production DB.
// Keep this tiny and grow it only when you see a real mismatch.
const NAME_FIXES = {
  bulbassauro: 'bulbasaur',
  ivyssauro: 'ivysaur',
  venossauro: 'venusaur',
  alakazan: 'alakazam',
  beyleef: 'bayleef',
  clefary: 'clefairy',
  graveller: 'graveler',
  'mr. mime': 'mr-mime',
  'mr mime': 'mr-mime',
  'nidoran ♂': 'nidoran-m',
  'nidoran ♀': 'nidoran-f',
  'nidoran m': 'nidoran-m',
  'nidoran f': 'nidoran-f',
  chicorita: 'chikorita',
  primeape: 'primeape',
  charizard: 'charizard',
};

const normalize = (raw) => {
  if (!raw) return '';
  let s = raw.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  s = s.trim().toLowerCase();
  // direct fix table
  if (NAME_FIXES[s]) return NAME_FIXES[s];
  // generic clean-up: spaces and dots → dashes
  s = s.replace(/[\s.]+/g, '-');
  return NAME_FIXES[s] || s;
};

/**
 * Find a custom-uploaded GIF whose name matches `name` (case/accent-insensitive).
 * Looks at `uploadedGifsCache.speciesNames[class][position]{stage_X}` and
 * returns the URL of the GIF at the matching slot+stage.
 */
const findCustomGifByName = (name, cache) => {
  if (!cache?.speciesNames) return null;
  const target = normalize(name);
  if (!target) return null;

  const sn = cache.speciesNames;
  for (const cls of Object.keys(sn)) {
    const positions = sn[cls] || {};
    for (const pos of Object.keys(positions)) {
      const stages = positions[pos] || {};
      for (const stageN of [1, 2, 3]) {
        const cfgName = stages[`stage_${stageN}`];
        if (cfgName && normalize(cfgName) === target) {
          // Look up the actual URL for this (cls, pos, stage)
          const url = cache?.[cls]?.[pos]?.[stageN];
          if (url) return typeof url === 'string' ? url : url.url || null;
        }
      }
    }
  }
  return null;
};

/**
 * Builds the ImageKit CDN URL for a Pokémon name (lowercase + .gif).
 * The URL changes automatically when the student's monster.name is updated
 * by the auto-evolution system (levels 16 and 32) — no extra wiring needed.
 */
export const resolveImageKitUrl = (name) => {
  const n = normalize(name);
  return n ? `${IMAGEKIT_URL}${n}.gif` : null;
};

/**
 * Returns the best sprite URL for a Pokémon name, or null if nothing matches.
 *
 * @param {string} name  Pokémon name (e.g. "Charmeleon", "Ralts", "Bulbassauro")
 * @param {object} cache `uploadedGifsCache` from helpers.js
 * @param {object} [opts]
 *   - preferStatic: if true, prefer the static PNG from PokeAPI when no
 *                   custom GIF is found (useful for thumbnails)
 */
export const resolveSpriteUrl = (name, cache, opts = {}) => {
  if (!name) return null;

  // 1) Custom upload wins
  const custom = findCustomGifByName(name, cache);
  if (custom) return custom;

  // 2) PokeAPI sprite by Pokédex ID
  const normalized = normalize(name);
  const id = pokemonIdMap[normalized];
  if (!id) return null;

  if (opts.preferStatic) {
    return `${POKEAPI_STATIC}/${id}.png`;
  }
  // Animated GIFs only exist for gen 1-5 (IDs 1-649). Fall back to static
  // for newer Pokémon so the card never goes blank.
  if (id <= 649) {
    return `${POKEAPI_ANIMATED}/${id}.gif`;
  }
  return `${POKEAPI_STATIC}/${id}.png`;
};

/**
 * Convenience helper for static thumbnails (Pokédex, mural, etc).
 */
export const resolveStaticSpriteUrl = (name, cache) =>
  resolveSpriteUrl(name, cache, { preferStatic: true });

export default resolveSpriteUrl;
